import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ptBR from '../locales/pt-BR.json';
import enUS from '../locales/en-US.json';

const LANGUAGE_KEY = 'preferredLanguage';
const isDevelopment = process.env.NODE_ENV === 'development';

// Track missing translations in development
const missingTranslations = new Set();

// Get stored language preference
const getStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    if (stored === 'pt-BR' || stored === 'en-US') {
      return stored;
    }
  } catch (e) {
    console.error('Error reading language from localStorage:', e);
  }
  return null;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'pt-BR': { translation: ptBR },
      'en-US': { translation: enUS }
    },
    lng: getStoredLanguage() || undefined,
    fallbackLng: 'en-US',
    supportedLngs: ['pt-BR', 'en-US'],
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_KEY,
      caches: ['localStorage']
    },
    react: {
      useSuspense: false
    },
    // Missing key handler for development debugging
    saveMissing: isDevelopment,
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (isDevelopment && !missingTranslations.has(key)) {
        missingTranslations.add(key);
        console.warn(`[i18n] Missing translation key: "${key}" for languages: ${lngs.join(', ')}`);
      }
    },
    // In development, return key with visual indicator for missing translations
    parseMissingKeyHandler: (key) => {
      if (isDevelopment) {
        return `⚠️ ${key}`;
      }
      return key;
    }
  });

// Helper to check if language is set
export const isLanguageSet = () => {
  return getStoredLanguage() !== null;
};

// Helper to set language
export const setLanguage = (lang) => {
  if (lang === 'pt-BR' || lang === 'en-US') {
    localStorage.setItem(LANGUAGE_KEY, lang);
    i18n.changeLanguage(lang);
    return true;
  }
  return false;
};

// Helper to get current language
export const getCurrentLanguage = () => {
  return i18n.language || 'en-US';
};

// Helper to get all missing translations (for development debugging)
export const getMissingTranslations = () => {
  return Array.from(missingTranslations);
};

// Helper to check if a translation exists
export const hasTranslation = (key) => {
  return i18n.exists(key);
};

export default i18n;
